import type { CardsProps } from '@risk-smart/themed-cloudscape-components/cards';
import Cards from '@risk-smart/themed-cloudscape-components/cards';
import Container from '@risk-smart/themed-cloudscape-components/container';
import Header from '@risk-smart/themed-cloudscape-components/header';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { Icon } from '@risksmart-app/atomic-ui';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import type { FC, MouseEvent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  DrillCount,
  DrillDownColumnHeader,
} from 'src/blocks/drill-down-column';
import { RevealButton } from 'src/blocks/object-toolbar/parts/RevealButton';
import { Permission } from 'src/rbac/Permission';

import Link from '@/components/link';
import { getFriendlyId } from '@/utils/friendlyId';
import { internalAuditAddUrl, internalAuditDetailsUrl } from '@/utils/urls';

import type { InternalAuditRegisterFields } from '../types';
import styles from './style.module.scss';

type CardItem = {
  id: string;
  title: string;
  rating: { color: string; label: string; value: number } | null;
  SequentialId: null | number | undefined;
  isBusinessArea: boolean;
};
export interface Props {
  loading: boolean;
  type: 'businessArea' | 'internalAuditEntity';
  /** The item selected in the PARENT column, whose children this shows. */
  selectedId?: string;
  /** The item selected in THIS column — drives the card highlight. */
  selectedItemId?: string;
  onSelectAction: (
    id: string,
    type: 'businessArea' | 'internalAuditEntity'
  ) => void;
  internalAudits: readonly InternalAuditRegisterFields[];
}
interface RatingsByValue {
  [key: number]: { label: string; color: string; value: number };
}

const unlinkedId = 'unlinked';

const cardItemsFromInternalAuditBusinessAreaData = (
  internalAudits: readonly InternalAuditRegisterFields[]
) => {
  const businessAreas = Array.from(
    new Map(
      internalAudits.map((item) => [item['businessArea']?.Id, item])
    ).values()
  );

  const cardItems: CardItem[] = businessAreas
    .filter((c) => c.businessArea)
    .map((item) => ({
      id: item.businessArea!.Id,
      title: item.businessArea!.Title,
      SequentialId: item.businessArea!.SequentialId,
      rating: null,
      isBusinessArea: true,
    }));

  return cardItems;
};

const cardItemsFromInternalAuditData = (
  internalAudits: readonly InternalAuditRegisterFields[],
  ratings: RatingsByValue,
  selectedId: string | undefined
) => {
  const cardItems: CardItem[] = internalAudits
    .filter((c) => c.businessArea?.Id === selectedId)
    .map((item) => ({
      id: item.Id,
      title: item.Title,
      SequentialId: item.SequentialId,
      rating: ratings[item.AuditRating || 0],
      isBusinessArea: false,
    }));

  return cardItems;
};

const Type: FC<Props> = ({
  type,
  onSelectAction,
  internalAudits,
  selectedId,
  selectedItemId,
  loading,
}) => {
  /*
   * The compact action is a real anchor, so route a plain left click through the
   * router and leave modifier clicks to the browser. The `from` state was
   * `compliance-dashboard` here, copied along with the rest of the clone — this is the
   * Internal Audit dashboard.
   */
  const navigate = useNavigate();
  const followInternally = (event: MouseEvent<HTMLElement>) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    navigate(internalAuditAddUrl(), {
      state: { from: 'internal-audit-dashboard' },
    });
  };

  const { t } = useTranslation(['common'], {
    keyPrefix: 'internalAudits',
  });
  const { t: tc } = useTranslation(['common']);
  const { t: tr } = useTranslation(['ratings']);

  const cardItems = useMemo<CardItem[]>(() => {
    const ratingsByValue: RatingsByValue = [
      ...tr('internal_audit_report_outcome'),
    ].reduce((sorted, res) => ({ ...sorted, [res.value]: res }), {});
    if (type === 'businessArea') {
      return cardItemsFromInternalAuditBusinessAreaData(internalAudits);
    }

    return cardItemsFromInternalAuditData(
      internalAudits,
      ratingsByValue,
      selectedId
    );
  }, [internalAudits, selectedId, tr, type]);

  /*
   * Three distinct states, not one. The audit-entity column with no business area
   * selected has not "found no items" — it is waiting for the drill-down step to its
   * left, and saying so is what makes the cascade discoverable.
   */
  const awaitingParentSelection = type !== 'businessArea' && !selectedId;
  const empty = awaitingParentSelection
    ? t('dashboard.select_prompt').internalAuditEntity
    : tc('noItemsFound');

  /*
   * How many audit entities sit under each business area, so a card can show what
   * drilling into it will yield. Only the business-area column drills anywhere.
   */
  const childCountByBusinessAreaId = new Map<string, number>();
  if (type === 'businessArea') {
    for (const audit of internalAudits) {
      const areaId = audit.businessArea?.Id;
      if (!areaId) {
        continue;
      }
      childCountByBusinessAreaId.set(
        areaId,
        (childCountByBusinessAreaId.get(areaId) ?? 0) + 1
      );
    }
  }

  /*
   * Selection is derived from the page's drill-down state rather than held locally.
   * It used to be local state cleared by an effect whenever `selectedId` changed — a
   * workaround for a card staying highlighted under a parent it no longer belonged to,
   * rather than a fix for it.
   */
  const selectedItems = cardItems.filter((item) => item.id === selectedItemId);

  const onSelectionChange: CardsProps<CardItem>['onSelectionChange'] = ({
    detail,
  }) => {
    // Cloudscape types this as an array, and a deselect would arrive empty.
    const [selected] = detail.selectedItems;
    if (!selected) {
      return;
    }

    onSelectAction(selected.id, type);
  };

  return (
    // `universe-{type}` is the e2e hook for this drill-down column, mirroring
    // `tier-{n}` on the risk dashboard. The visible headings are i18n-driven and
    // per-org taxonomy-overridable, so specs cannot select on them.
    <div className={styles.type} data-testid={`universe-${type}`}>
      <Container fitHeight variant={'stacked'}>
        <SpaceBetween direction={'vertical'} size={'m'}>
          <DrillDownColumnHeader
            title={t('dashboard_category_titles')[type] || ''}
            counter={
              awaitingParentSelection || cardItems.length === 0
                ? undefined
                : `(${cardItems.length})`
            }
            action={
              type === 'businessArea' ? undefined : (
                <Permission permission={'insert:internal_audit_entity'}>
                  {/*
                    Compact icon rather than a pill: a ~96px pill cannot share a
                    drill-down column's header row without squeezing the title (see
                    COI-739). The label names the object so the icon is never unnamed.
                  */}
                  <RevealButton
                    label={t('dashboard.add_labelled')}
                    reveal={'none'}
                    href={internalAuditAddUrl()}
                    onClick={followInternally}
                  >
                    <Icon name={'plus'} size={'lg'} />
                  </RevealButton>
                </Permission>
              )
            }
          />
          <Cards<CardItem>
            ariaLabels={{
              itemSelectionLabel: (e, n) =>
                t('dashboard.select', { title: n.title }),
              selectionGroupLabel: t('dashboard.item_selection'),
            }}
            entireCardClickable={true}
            cardDefinition={{
              sections: [
                {
                  id: 'title',
                  content: (item) => {
                    const childCount =
                      childCountByBusinessAreaId.get(item.id) ?? 0;

                    return (
                      <div
                        className={
                          'flex w-full items-center justify-between gap-2'
                        }
                      >
                        <div className={'inline-block'}>
                          <Link
                            variant={'secondary'}
                            href={
                              item.id === unlinkedId || item.isBusinessArea
                                ? undefined
                                : internalAuditDetailsUrl(item.id)
                            }
                          >
                            <Header
                              variant={'h3'}
                              data-unlinked={item.id === unlinkedId}
                            >
                              <span className={'text-base'}>{item.title}</span>
                            </Header>
                          </Link>
                        </div>
                        {/* Absent on a card with no children, so a dead end reads
                            as one. */}
                        {childCount > 0 && (
                          <DrillCount
                            count={childCount}
                            label={t('dashboard.child_count', {
                              count: childCount,
                            })}
                          />
                        )}
                      </div>
                    );
                  },
                },
              ],
              header: (item) =>
                item.id === unlinkedId ? null : (
                  <div className={'flex'}>
                    {item.id !== unlinkedId && (
                      <div className={'text-grey text-sm flex-grow'}>
                        {getFriendlyId(
                          !item.isBusinessArea
                            ? Parent_Type_Enum.InternalAuditEntity
                            : Parent_Type_Enum.BusinessArea,
                          item.SequentialId
                        )}
                      </div>
                    )}

                    {item.rating !== null && (
                      <SimpleRatingBadge rating={item.rating} />
                    )}
                  </div>
                ),
            }}
            cardsPerRow={[{ cards: 1 }]}
            items={cardItems}
            empty={empty}
            loading={loading}
            loadingText={t('loading_message')}
            visibleSections={['title']}
            selectionType={'single'}
            selectedItems={selectedItems}
            onSelectionChange={onSelectionChange}
            trackBy={(item) => item.id ?? item.title}
          />
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default Type;
